import { useRef, useState, useCallback, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react'
import { cn } from '@/lib/utils'

interface PinInputProps {
  length?: number
  onChange: (pin: string) => void
  onComplete?: (pin: string) => void
  autoFocus?: boolean
  disabled?: boolean
  className?: string
}

export function PinInput({ length = 6, onChange, onComplete, autoFocus = false, disabled, className }: PinInputProps) {
  const [values, setValues] = useState<string[]>(Array(length).fill(''))
  const inputs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (autoFocus) inputs.current[0]?.focus()
  }, [autoFocus])

  const update = useCallback((newVals: string[]) => {
    setValues(newVals)
    const pin = newVals.join('')
    onChange(pin)
    if (pin.length === length && !newVals.includes('') && onComplete) {
      onComplete(pin)
    }
  }, [onChange, onComplete, length])

  const handleChange = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1)
    const next = [...values]
    next[idx] = digit
    update(next)
    if (digit && idx < length - 1) inputs.current[idx + 1]?.focus()
  }

  const handleKey = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !values[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && idx > 0) inputs.current[idx - 1]?.focus()
    if (e.key === 'ArrowRight' && idx < length - 1) inputs.current[idx + 1]?.focus()
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!digits) return
    const next = Array(length).fill('')
    digits.split('').forEach((d, i) => { next[i] = d })
    update(next)
    const focusIdx = Math.min(digits.length, length - 1)
    inputs.current[focusIdx]?.focus()
  }

  return (
    <div className={cn('flex gap-2', className)}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el }}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={values[i]}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          className={cn(
            'h-10 w-10 rounded-lg border border-[#374151] bg-[#1f2937] text-center text-lg font-bold text-white',
            'focus:border-[#f59e0b] focus:outline-none',
            'disabled:opacity-50',
          )}
        />
      ))}
    </div>
  )
}
